import { useMemo } from 'react';

import { useTranslation } from '@/hooks/useTranslation';
import { ValidationStatistic } from '@/pages/browse/hooks/useValidationStatistics';
import { BrowseActiveFilter, FILTER_TYPE } from '@/pages/browse/types';

import { matchesAnyText, matchesFilterQuery } from './filterQuery';
import { FilterMenuItem } from './FilterMenuItem';
import { FilterSubmenu } from './FilterSubmenu';

interface ValidationFilterOption {
    key: string;
    text: string;
    searchKey: 'errorKey' | 'warningKey';
    type: string;
}

interface FilterListValidationProps {
    errors?: ValidationStatistic[];
    warnings?: ValidationStatistic[];
    query: string;
    onSelect: (type: string, filter: BrowseActiveFilter) => void;
    /** Angular renders errors, then bulk subscriptions, then warnings. */
    section: 'errors' | 'warnings';
}

function toOptions(
    statistics: ValidationStatistic[],
    searchKey: ValidationFilterOption['searchKey'],
    type: string,
    translate: (key: string) => string,
): ValidationFilterOption[] {
    return [...statistics]
        .map((statistic) => ({
            key: statistic.key,
            text: translate(statistic.key),
            searchKey,
            type,
        }))
        .sort((a, b) => a.text.localeCompare(b.text));
}

export function FilterListValidation({
    errors = [],
    warnings = [],
    query,
    onSelect,
    section,
}: FilterListValidationProps) {
    const { t } = useTranslation();

    const errorOptions = useMemo(
        () => toOptions(errors, 'errorKey', FILTER_TYPE.VALIDATION_ERRORS, t),
        [errors, t],
    );
    const warningOptions = useMemo(
        () => toOptions(warnings, 'warningKey', FILTER_TYPE.VALIDATION_WARNINGS, t),
        [warnings, t],
    );

    const optionMatches = (option: ValidationFilterOption) =>
        matchesAnyText(query, option.text, option.key);

    const matchingErrors = errorOptions.filter(optionMatches);
    const matchingWarnings = warningOptions.filter(optionMatches);
    const errorHeaderMatches = matchesFilterQuery(t('ITEMS_SEARCH_FILTER.MENU.ERRORS'), query);
    const warningHeaderMatches = matchesFilterQuery(t('ITEMS_SEARCH_FILTER.MENU.WARNINGS'), query);
    const visibleErrors = errorHeaderMatches ? errorOptions : matchingErrors;
    const visibleWarnings = warningHeaderMatches ? warningOptions : matchingWarnings;

    const selectOption = (option: ValidationFilterOption) => {
        onSelect(option.type, {
            name: option.text,
            type: option.type,
            itemsQuery: { [option.searchKey]: option.key },
            searchKey: option.searchKey,
        });
    };

    if (section === 'errors') {
        return (
            <FilterSubmenu
                label={t('ITEMS_SEARCH_FILTER.MENU.ERRORS')}
                query={query}
                matchesQuery={errorHeaderMatches}
                hasMatchingChildren={matchingErrors.length > 0}
            >
                {visibleErrors.map((option) => (
                    <li key={`error-${option.key}`} className="tw-list-none">
                        <FilterMenuItem className="tw-pl-8" onSelect={() => selectOption(option)}>
                            {option.text}
                        </FilterMenuItem>
                    </li>
                ))}
            </FilterSubmenu>
        );
    }

    return (
        <FilterSubmenu
            label={t('ITEMS_SEARCH_FILTER.MENU.WARNINGS')}
            query={query}
            matchesQuery={warningHeaderMatches}
            hasMatchingChildren={matchingWarnings.length > 0}
        >
            {visibleWarnings.map((option) => (
                <li key={`warning-${option.key}`} className="tw-list-none">
                    <FilterMenuItem className="tw-pl-8" onSelect={() => selectOption(option)}>
                        {option.text}
                    </FilterMenuItem>
                </li>
            ))}
        </FilterSubmenu>
    );
}
